import React from 'react';
import { Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { logout } from '@/store/thunkMidddleware/authMiddleware';
import { cn } from '@/lib/utils';
import Sidebar from './Sidebar';
import Header from './Header';

/**
 * The application frame: rail on the left, thin header on top, page below.
 *
 * The collapsed state is remembered, because a vendor who works from the icon
 * rail wants it every time, not once. Mobile gets a drawer rather than a rail —
 * there is no width for a 64px rail plus content on a phone.
 */

const COLLAPSE_KEY = 'byz.shell.collapsed';
const THEME_KEY = 'theme';

const AppShell: React.FC = () => {
  const dispatch = useAppDispatch();
  const { vendorProfile } = useAppSelector((state) => state.profile);

  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(
    () => localStorage.getItem(THEME_KEY) === 'dark',
  );

  React.useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem(THEME_KEY, darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const profile = vendorProfile?.payload;
  const profileComplete = Boolean(
    profile?.vendorProfileCompleted && (profile?.profilePercentage ?? 0) >= 100,
  );

  const handleLogout = async () => {
    const response = await dispatch(logout());
    if (logout.fulfilled.match(response)) window.location.reload();
  };

  return (
    <div className="min-h-screen bg-flup-canvas">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        profileComplete={profileComplete}
        vendorName={profile?.name || profile?.fullName || profile?.businessName}
        vendorRole={profile?.businessName ? 'Venue owner' : undefined}
        vendorAvatar={profile?.profileImage || profile?.avatar}
        darkMode={darkMode}
        onToggleDarkMode={() => setDarkMode((v) => !v)}
        onLogout={handleLogout}
      />

      <div
        className={cn(
          'flex flex-col min-h-screen transition-[padding] duration-200',
          collapsed ? 'lg:pl-16' : 'lg:pl-64',
        )}
      >
        <Header onOpenMobileNav={() => setMobileOpen(true)} />

        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
